import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Plus, X, Trash, Lock, Briefcase, Wrench, Stack } from "@phosphor-icons/react";

const CATS = ["vacation", "court", "inspection", "meeting", "holiday", "reminder", "other"];
const catColor = (c) => ({
  vacation: "bg-cyan-500", court: "bg-red-600", inspection: "bg-fuchsia-500",
  meeting: "bg-slate-700", holiday: "bg-emerald-500", reminder: "bg-amber-500",
  oncall: "bg-red-600",
}[c] || "bg-slate-500");

const VIEWS = [
  { key: "combined",    label: "Combined",   Icon: Stack,     accent: "bg-slate-900" },
  { key: "management",  label: "Management", Icon: Briefcase, accent: "bg-cyan-700" },
  { key: "maintenance", label: "Maintenance",Icon: Wrench,    accent: "bg-fuchsia-700" },
];

export default function TeamCalendar() {
  const [view, setView] = useState(() => localStorage.getItem("fr_cal_view") || "combined");
  const [events, setEvents] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [show, setShow] = useState(false);
  const load = async (v = view) => {
    try {
      const r = await api.get(`/office/calendar?view=${encodeURIComponent(v)}`);
      setEvents(r.data.events || []);
      setCanEdit(!!r.data.can_edit);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(view); localStorage.setItem("fr_cal_view", view); /* eslint-disable-next-line */ }, [view]);
  const del = async (id) => {
    if (id.startsWith("oncall-")) return toast.info("On-call shifts are managed in Emergency Protocol");
    if (!window.confirm("Delete event?")) return;
    try { await api.delete(`/office/calendar/${id}`); toast.success("Deleted"); load(view); }
    catch { toast.error("Failed"); }
  };
  const scopePill = (s) => {
    if (s === "management") return <span className="px-1.5 h-4 leading-4 bg-cyan-700 text-white text-[8px] font-black uppercase tracking-widest">MGMT</span>;
    if (s === "maintenance") return <span className="px-1.5 h-4 leading-4 bg-fuchsia-700 text-white text-[8px] font-black uppercase tracking-widest">MAINT</span>;
    return <span className="px-1.5 h-4 leading-4 bg-slate-500 text-white text-[8px] font-black uppercase tracking-widest">BOTH</span>;
  };
  const bucketed = countByEffScope(events);

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="team-calendar-page">
        <div>
          <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
            <Calendar weight="fill" className="text-cyan-600"/> Team Calendar
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Filter by <b>Management</b> (court dates, meetings, vacations, evictions), <b>Maintenance</b>
            (inspections, on-call shifts) — or see everything <b>Combined</b>.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-500" htmlFor="cal-view">View</label>
          <select id="cal-view" value={view} onChange={e => setView(e.target.value)} data-testid="calendar-view-select"
            className="h-10 px-3 border-2 border-slate-900 bg-white text-sm font-black uppercase tracking-widest shadow-[3px_3px_0_#0f172a]">
            {VIEWS.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
          </select>
          <div className="hidden md:flex gap-1 ml-2">
            {VIEWS.map(v => {
              const active = v.key === view;
              const I = v.Icon;
              return (
                <button key={v.key} onClick={() => setView(v.key)} data-testid={`view-chip-${v.key}`}
                  className={`h-10 px-3 border-2 border-slate-900 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1 ${
                    active ? `${v.accent} text-white shadow-[3px_3px_0_#0f172a]` : "bg-white hover:bg-slate-100"}`}>
                  <I size={14} weight="fill"/> {v.label}
                  <span className="ml-1 text-[9px] opacity-80">{bucketed[v.key] ?? 0}</span>
                </button>
              );
            })}
          </div>
          {canEdit && (
            <button onClick={() => setShow(true)} data-testid="new-event-btn"
              className="h-10 px-3 bg-cyan-600 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1 ml-auto">
              <Plus size={14}/> Add Event
            </button>
          )}
        </div>

        {!canEdit && (
          <div className="p-2 bg-amber-50 border-l-4 border-amber-500 text-[11px] flex items-center gap-2">
            <Lock size={14}/> Read-only — owner-level access required to add or edit.
          </div>
        )}

        {events.length === 0 ? (
          <div className="rugged-card p-6 text-center text-sm text-slate-500">
            No events in <b>{VIEWS.find(v => v.key === view)?.label}</b> view.
          </div>
        ) : (
          <div className="space-y-2">
            {events.map(e => (
              <div key={e.id} className="p-3 rugged-card flex items-start gap-3 flex-wrap" data-testid={`event-${e.id}`}>
                <div className="flex items-center gap-1 shrink-0">
                  <div className={`px-2 h-6 leading-6 ${catColor(e.category)} text-white font-black uppercase tracking-widest text-[9px]`}>{e.category}</div>
                  {scopePill(e.effective_scope || e.scope || "both")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-black text-sm">{e.title}</div>
                  <div className="text-[10px] text-slate-600">
                    {(e.starts_at || "").slice(0, 16).replace("T", " ")}
                    {e.ends_at && e.ends_at !== e.starts_at && ` → ${e.ends_at.slice(0, 16).replace("T", " ")}`}
                    {e.attendee_names?.length > 0 && ` · ${e.attendee_names.join(", ")}`}
                  </div>
                  {e.notes && <div className="text-[11px] text-slate-700 mt-1">{e.notes}</div>}
                  {e.source === "oncall" && <div className="text-[9px] text-red-700 uppercase tracking-widest mt-0.5">from Emergency Protocol</div>}
                  {e.source === "eviction_auto" && <div className="text-[9px] text-red-700 uppercase tracking-widest mt-0.5">auto from eviction case</div>}
                </div>
                {canEdit && !e.id.startsWith("oncall-") && (
                  <button onClick={() => del(e.id)} className="h-8 w-8 border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
                    <Trash size={14}/>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {show && <EventModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(view); }}/>}
      </div>
    </AdminShell>
  );
}

function countByEffScope(events) {
  const out = { combined: events.length, management: 0, maintenance: 0 };
  for (const e of events) {
    const s = e.effective_scope || e.scope || "both";
    if (s === "management" || s === "both") out.management += 1;
    if (s === "maintenance" || s === "both") out.maintenance += 1;
  }
  return out;
}

function EventModal({ onClose, onSaved }) {
  const [f, setF] = useState({
    title: "", category: "meeting", scope: "both",
    starts_at: "", ends_at: "", all_day: false, notes: "",
  });
  const [attendeesText, setAT] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title || !f.starts_at) return toast.error("Title + start required");
    setBusy(true);
    try {
      await api.post("/office/calendar", {
        ...f,
        starts_at: new Date(f.starts_at).toISOString(),
        ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : "",
        attendee_names: attendeesText.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Added"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="event-modal">
      <div className="bg-white w-full max-w-lg border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-3 bg-cyan-600 text-white flex items-start justify-between">
          <div className="font-black uppercase tracking-widest">Add Event</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-4 space-y-2">
          <input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} placeholder="Title *" data-testid="event-title" className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Category
              <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })} className="h-10 px-3 border-2 border-slate-300 text-sm">
                {CATS.map(c => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Calendar
              <select value={f.scope} onChange={e => setF({ ...f, scope: e.target.value })} data-testid="event-scope" className="h-10 px-3 border-2 border-slate-300 text-sm">
                <option value="both">Both (shows everywhere)</option>
                <option value="management">Management only</option>
                <option value="maintenance">Maintenance only</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="datetime-local" value={f.starts_at} onChange={e => setF({ ...f, starts_at: e.target.value })} data-testid="event-start" className="h-10 px-2 border-2 border-slate-300 text-sm"/>
            <input type="datetime-local" value={f.ends_at} onChange={e => setF({ ...f, ends_at: e.target.value })} className="h-10 px-2 border-2 border-slate-300 text-sm"/>
          </div>
          <input value={attendeesText} onChange={e => setAT(e.target.value)} placeholder="Attendees (comma separated)" className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={2} placeholder="Notes" className="w-full p-2 border-2 border-slate-300 text-sm"/>
          <button onClick={save} disabled={busy} data-testid="event-save"
            className="w-full h-11 bg-cyan-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : "Add Event"}
          </button>
        </div>
      </div>
    </div>
  );
}
